/**
 * Which engine drafts hold input (Option C, C1; moved out of `useEngineDrafts` in C3): the rail's dots, the overlay-click
 * guard, the funnel's `had_input` and — C3 — whether an import that completes on top of the dialog may close it. Pure
 * over the draft cache and the live form; the cache stays owned by `useEngineDrafts`.
 */
import type { ConnectionFormValues, DatabaseEngine } from '../../components/connectionFormSchema';
import { isSharedField, sameValues, scopedValuesOf, sharedValuesOf, type EngineDraft } from './engineDrafts';

export type MetaFlags = Record<string, { isDirty?: boolean } | undefined>;

type LiveForm = { state: { values: ConnectionFormValues; fieldMeta: unknown } };

/** Whether the user (or a patch) changed any engine-scoped field in this meta. */
export function scopedInput(meta: MetaFlags): boolean {
  return Object.entries(meta).some(([field, flags]) => !isSharedField(field) && flags?.isDirty === true);
}

/** Whether a shared field (Name, Color, AI permission) was changed. */
export function sharedInput(meta: MetaFlags): boolean {
  return Object.entries(meta).some(([field, flags]) => isSharedField(field) && flags?.isDirty === true);
}

/** The engines whose draft has input; the live engine counts only while it is attached (`detached` false). */
export function enginesWithInput(cache: ReadonlyMap<DatabaseEngine, EngineDraft>, detached: boolean, form: LiveForm): Set<DatabaseEngine> {
  const engines = new Set<DatabaseEngine>();
  for (const [engine, draft] of cache) if (scopedInput(draft.meta)) engines.add(engine);
  if (!detached) {
    const live = form.state.values.engine;
    if (scopedInput(form.state.fieldMeta as MetaFlags)) engines.add(live);
    else engines.delete(live);
  }
  return engines;
}

/**
 * Whether anything typed or patched differs from `baseline` (the form at open, after a prefill): a shared field, or any
 * engine draft with input — the baseline's engine compared by value (an untouched prefill is no input), every other
 * engine by its input flags, whether it is on screen or cached (C1 review C1-04).
 */
export function changedSinceBaseline(cache: ReadonlyMap<DatabaseEngine, EngineDraft>, detached: boolean, form: LiveForm, baseline: ConnectionFormValues): boolean {
  const live = form.state.values;
  if (sharedInput(form.state.fieldMeta as MetaFlags) && !sameValues(sharedValuesOf(live), sharedValuesOf(baseline))) return true;
  for (const engine of enginesWithInput(cache, detached, form)) {
    if (engine !== baseline.engine) return true;
    const values = !detached && live.engine === engine ? scopedValuesOf(live) : cache.get(engine)?.values;
    if (!sameValues(values, scopedValuesOf(baseline))) return true;
  }
  return false;
}
